import { useQuickscreenApplicationLogic } from "./hooks/useQuickscreenApplicationLogic";
import {
  Application,
  ApplicationForm,
  ApplicationHeader,
  ApplicationSectionList,
  SectionSchema,
} from "@certn/application";
import { Address, Text } from "@certn/cascadia";
import {
  BrandProvider,
  Container,
  Layout,
  Loader,
  NavigationSidebar,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import constants from "../../../constants";
import { AppLogo } from "../../components/app-header/AppLogo";
import { ApplicationSummary } from "./ApplicationSummary";

export function QuickscreenApplication() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    data,
    mutate,
    handleSave,
    handleSubmit,
    enableFpCoBrandingV2,
    caseId,
    validatedSectionName,
  } = useQuickscreenApplicationLogic();

  if (!data) {
    return <Loader overlay />;
  }

  const applicationContent = (
    <Application
      id={caseId ?? ""}
      locale={i18n.language}
      schema={data.data_schema}
      uischema={data.ui_schema as any}
      currentSection={validatedSectionName}
      data={data.applicant_form_data}
      onRefresh={() => {
        mutate();
      }}
      onNavigateToSection={(
        section: string | SectionSchema,
        type?: "replace" | "navigate"
      ) => {
        navigate(
          `/quickscreen/${caseId}/${typeof section === "string" ? section : section.name}`,
          {
            replace: type === "replace",
          }
        );
      }}
      onSubmitApplication={handleSubmit}
      onSave={handleSave}
      customerName={data.customer_name ?? ""}
      clientGroupName={data.client_group_name ?? ""}
      clientGroupAddress={data.client_group_address as Address}
      environment={constants.MODE}
      generatePresignedUrlEndpoint="/api/internal/client-portal/quickscreen-case/generate-presigned-url/"
      downloadUrl={`/api/internal/client-portal/quickscreen-case/${caseId}/file/download/`}
      sectionStatuses={data.section_statuses ?? {}}
      branding={data.co_branding || {}}
      summaryComponent={ApplicationSummary}
      basePath="quickscreen"
    >
      <Layout
        menuPosition="left"
        layout="applicant/narrow"
        header={
          <ApplicationHeader logo={<AppLogo />} exitRedirectPath="/cases" />
        }
        navigation={
          <NavigationSidebar
            toolbar={
              <div className={css({ maxWidth: 92, maxHeight: 27 })}>
                <AppLogo />
              </div>
            }
            width="wide"
          >
            {data.customer_name && (
              <div className={styles.titleContainer}>
                <Text style="summary/section-title">
                  <Trans>APPLICATION FOR</Trans>
                </Text>
                <Text
                  style="summary/content-primary"
                  testID="application-navigation-panel-team-name"
                >
                  {data.customer_name}
                </Text>
              </div>
            )}
            <ApplicationSectionList
              isSummaryCompleted={data.section_statuses?.summary === "COMPLETE"}
            />
          </NavigationSidebar>
        }
      >
        <Container>
          <ApplicationForm />
        </Container>
      </Layout>
    </Application>
  );

  return enableFpCoBrandingV2 ? (
    <BrandProvider
      primaryColor={
        typeof data.co_branding?.org_primary_color === "string"
          ? data.co_branding.org_primary_color
          : undefined
      }
    >
      {applicationContent}
    </BrandProvider>
  ) : (
    applicationContent
  );
}

const styles = {
  titleContainer: css({
    display: "flex",
    flexDirection: "column",
    height: "40px",
    paddingTop: "var(--space-6)",
    paddingBottom: "var(--space-6)",
    paddingLeft: "var(--space-5)",
    paddingRight: "var(--space-4)",
  }),
};
